import { useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { useDocumentList } from 'shared/_components/list/use-document-list';
import { DocType } from 'shared/constants/docs';
import ProjectListItem from 'shared/_model/project/project-list/project-list-item';
import { ListAtomValue } from 'shared/_components/list/types';
import { TDocProject } from 'shared/model/project';
import { TDocument } from 'shared/model/doc-base';
import ProjectListFilters from 'shared/_model/project/project-list/project-list-filters';
import ProjectCreate from 'shared/_model/project/project-create';
import { getProjectTotals } from 'shared/_model/project/project-list/project-list-item/utils';
import ProjectListActionsLeft from 'shared/_model/project/project-list/project-list-actions-left';
import ProjectListActionsRight from 'shared/_model/project/project-list/project-list-actions-right';
import { projectListAtom, projectListSettingsAtom } from 'shared/_model/project/project-list/store';
import { Page, PageContentList, PageItem } from 'shared/_components/page/styles';
import PageHeader from 'shared/_components/page/page-header';

export default function ProjectList() {
	const [{ items, total }, setList] = useAtom(projectListAtom);
	const [showCreate, setShowCreate] = useState(false);
	const toggleCreateNewProject = useCallback(() => {
		setShowCreate((s) => !s);
	}, []);

	const handleRefresh = useCallback(
		async (value: ListAtomValue<TDocProject>, db: PouchDB.Database<TDocument>) => {
			if (!db) {
				return;
			}
			const totals = await getProjectTotals(
				value.items.map((it) => it._id),
				db,
			);

			setList({
				...value,
				items: value.items.map((it) => ({
					...it,
					_totals: {
						fileCount: totals[it._id],
					},
				})),
			});
		},
		[setList],
	);

	const { refresh } = useDocumentList({
		type: DocType.PROJECT,
		listAtom: projectListAtom,
		settingsAtom: projectListSettingsAtom,
		onRefresh: handleRefresh,
		doSetList: false,
	});

	return (
		<Page>
			<PageHeader
				count={total}
				actionsLeft={<ProjectListActionsLeft refresh={refresh} />}
				actionsRight={<ProjectListActionsRight toggleCreateNewProject={toggleCreateNewProject} />}
			>
				Projects
			</PageHeader>
			<ProjectListFilters />
			<PageContentList>
				{items.map((item) => (
					<PageItem key={item._id}>
						<ProjectListItem {...item} />
					</PageItem>
				))}
			</PageContentList>
			{showCreate && <ProjectCreate onClose={toggleCreateNewProject} />}
		</Page>
	);
}
